import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import AudioFolderPicker from './AudioFolderPicker';
import {
  loadDirectoryHandle,
  ensureReadWritePermission,
} from '../lib/audioFolderStore';
import { saveAudioToFolder } from '../lib/saveAudioToFolder';

type Provider = 'ekacare' | 'openai' | 'anthropic';

interface AudioRecorderProps {
  backend_uri: string;
  session_id: string;
  language: string;
  onTranscriptChange: (transcript: string) => void;
  onProcessStart: (provider?: string) => void;
  onOpenAIDataReady: (data: any) => void;
  onEkaDataReady: (data: any) => void;
  onAudioUrlReady: (url: string) => void;
}

/* ─────────────────────────────────────────────
   Siri-style waveform — all brand colours from
   the prana·aa logo palette
───────────────────────────────────────────── */
const WAVE_CSS = `
  @keyframes waveAnim1 {
    0%,100% { d: path("M0,40 C15,40 20,10 40,10 C60,10 65,70 85,70 C105,70 110,20 130,20 C150,20 155,60 175,60 C195,60 200,30 220,30 C240,30 245,55 265,55 C285,55 290,25 310,25 C330,25 335,50 355,50 C375,50 380,35 400,35"); }
    50%     { d: path("M0,40 C15,40 20,20 40,20 C60,20 65,60 85,60 C105,60 110,15 130,15 C150,15 155,65 175,65 C195,65 200,22 220,22 C240,22 245,58 265,58 C285,58 290,18 310,18 C330,18 335,62 355,62 C375,62 380,30 400,30"); }
  }
  @keyframes waveAnim2 {
    0%,100% { d: path("M0,40 C15,40 20,24 40,24 C60,24 65,56 85,56 C105,56 110,28 130,28 C150,28 155,52 175,52 C195,52 200,32 220,32 C240,32 245,48 265,48 C285,48 290,35 310,35 C330,35 335,45 355,45 C375,45 380,38 400,38"); }
    50%     { d: path("M0,40 C15,40 20,15 40,15 C60,15 65,65 85,65 C105,65 110,18 130,18 C150,18 155,62 175,62 C195,62 200,20 220,20 C240,20 245,60 265,60 C285,60 290,22 310,22 C330,22 335,58 355,58 C375,58 380,32 400,32"); }
  }
  @keyframes waveAnim3 {
    0%,100% { d: path("M0,40 C15,40 20,30 40,30 C60,30 65,50 85,50 C105,50 110,33 130,33 C150,33 155,47 175,47 C195,47 200,36 220,36 C240,36 245,44 265,44 C285,44 290,37 310,37 C330,37 335,43 355,43 C375,43 380,39 400,39"); }
    50%     { d: path("M0,40 C15,40 20,14 40,14 C60,14 65,66 85,66 C105,66 110,17 130,17 C150,17 155,63 175,63 C195,63 200,21 220,21 C240,21 245,59 265,59 C285,59 290,24 310,24 C330,24 335,56 355,56 C375,56 380,34 400,34"); }
  }
  @keyframes waveAnim4 {
    0%,100% { d: path("M0,40 C15,40 20,28 40,28 C60,28 65,52 85,52 C105,52 110,30 130,30 C150,30 155,50 175,50 C195,50 200,34 220,34 C240,34 245,46 265,46 C285,46 290,36 310,36 C330,36 335,44 355,44 C375,44 380,38 400,38"); }
    50%     { d: path("M0,40 C15,40 20,18 40,18 C60,18 65,62 85,62 C105,62 110,22 130,22 C150,22 155,58 175,58 C195,58 200,25 220,25 C240,25 245,55 265,55 C285,55 290,28 310,28 C330,28 335,52 355,52 C375,52 380,35 400,35"); }
  }
  @keyframes waveAnim5 {
    0%,100% { d: path("M0,40 C15,40 20,32 40,32 C60,32 65,48 85,48 C105,48 110,35 130,35 C150,35 155,45 175,45 C195,45 200,37 220,37 C240,37 245,43 265,43 C285,43 290,38 310,38 C330,38 335,42 355,42 C375,42 380,40 400,40"); }
    50%     { d: path("M0,40 C15,40 20,16 40,16 C60,16 65,64 85,64 C105,64 110,19 130,19 C150,19 155,61 175,61 C195,61 200,23 220,23 C240,23 245,57 265,57 C285,57 290,26 310,26 C330,26 335,54 355,54 C375,54 380,33 400,33"); }
  }
  @keyframes waveAnim6 {
    0%,100% { d: path("M0,40 C15,40 20,26 40,26 C60,26 65,54 85,54 C105,54 110,29 130,29 C150,29 155,51 175,51 C195,51 200,33 220,33 C240,33 245,47 265,47 C285,47 290,35 310,35 C330,35 335,45 355,45 C375,45 380,37 400,37"); }
    50%     { d: path("M0,40 C15,40 20,12 40,12 C60,12 65,68 85,68 C105,68 110,15 130,15 C150,15 155,65 175,65 C195,65 200,18 220,18 C240,18 245,62 265,62 C285,62 290,20 310,20 C330,20 335,60 355,60 C375,60 380,28 400,28"); }
  }
  @keyframes burstPulse {
    0%   { r: 10px; opacity: 0.7; }
    70%  { r: 42px; opacity: 0.1; }
    100% { r: 48px; opacity: 0;   }
  }
  @keyframes centerPulse {
    0%,100% { r: 4px; opacity: 1;   }
    50%     { r: 6px; opacity: 0.8; }
  }
  @keyframes recordingDot {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.45; }
  }

  .prana-wave { fill: none; stroke-linecap: round; stroke-linejoin: round; }
  .prana-w1 { stroke: #E56C98; stroke-width: 2.5; animation: waveAnim1 2.1s ease-in-out infinite; }
  .prana-w2 { stroke: #EB96B2; stroke-width: 2;   animation: waveAnim2 1.7s ease-in-out infinite 0.15s; }
  .prana-w3 { stroke: #97B88F; stroke-width: 2;   animation: waveAnim3 2.4s ease-in-out infinite 0.3s; }
  .prana-w4 { stroke: #D18899; stroke-width: 1.5; animation: waveAnim4 1.9s ease-in-out infinite 0.45s; }
  .prana-w5 { stroke: #81A969; stroke-width: 1.5; animation: waveAnim5 2.2s ease-in-out infinite 0.6s; }
  .prana-w6 { stroke: #E56C98; stroke-width: 1;   animation: waveAnim6 1.6s ease-in-out infinite 0.8s; opacity: 0.45; }

  .prana-burst { fill: none; }
  .prana-br1 { stroke: #E56C98; stroke-width: 1;   animation: burstPulse 2.4s ease-out infinite 0s; }
  .prana-br2 { stroke: #EB96B2; stroke-width: 0.8; animation: burstPulse 2.4s ease-out infinite 0.4s; }
  .prana-br3 { stroke: #97B88F; stroke-width: 0.7; animation: burstPulse 2.4s ease-out infinite 0.8s; }

  .prana-center { animation: centerPulse 1.4s ease-in-out infinite; }
`;

const SiriWaveform: React.FC = () => (
  <>
    <style>{WAVE_CSS}</style>
    <div
      style={{
        width: '100%',
        height: 100,
        borderRadius: 8,
        background: '#FFFFFF',
        border: '1px solid #DEE2F1',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        viewBox="0 0 400 80"
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%' }}
      >
        <defs>
          {/* Edge-fade mask so waves dissolve softly at left/right */}
          <linearGradient id="pranaEdgeFade" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="white" stopOpacity="1" />
            <stop offset="10%"  stopColor="white" stopOpacity="0" />
            <stop offset="90%"  stopColor="white" stopOpacity="0" />
            <stop offset="100%" stopColor="white" stopOpacity="1" />
          </linearGradient>
          <mask id="pranaWaveMask">
            <rect x="0" y="0" width="400" height="80" fill="white" />
            <rect x="0" y="0" width="400" height="80" fill="url(#pranaEdgeFade)" />
          </mask>
        </defs>

        <g mask="url(#pranaWaveMask)">
          {/* Siri burst rings */}
          <circle className="prana-burst prana-br3" cx="200" cy="40" r="10" />
          <circle className="prana-burst prana-br2" cx="200" cy="40" r="10" />
          <circle className="prana-burst prana-br1" cx="200" cy="40" r="10" />

          {/* Wave layers — back to front */}
          <path
            className="prana-wave prana-w6"
            d="M0,40 C15,40 20,26 40,26 C60,26 65,54 85,54 C105,54 110,29 130,29 C150,29 155,51 175,51 C195,51 200,33 220,33 C240,33 245,47 265,47 C285,47 290,35 310,35 C330,35 335,45 355,45 C375,45 380,37 400,37"
          />
          <path
            className="prana-wave prana-w5"
            d="M0,40 C15,40 20,32 40,32 C60,32 65,48 85,48 C105,48 110,35 130,35 C150,35 155,45 175,45 C195,45 200,37 220,37 C240,37 245,43 265,43 C285,43 290,38 310,38 C330,38 335,42 355,42 C375,42 380,40 400,40"
          />
          <path
            className="prana-wave prana-w4"
            d="M0,40 C15,40 20,28 40,28 C60,28 65,52 85,52 C105,52 110,30 130,30 C150,30 155,50 175,50 C195,50 200,34 220,34 C240,34 245,46 265,46 C285,46 290,36 310,36 C330,36 335,44 355,44 C375,44 380,38 400,38"
          />
          <path
            className="prana-wave prana-w3"
            d="M0,40 C15,40 20,30 40,30 C60,30 65,50 85,50 C105,50 110,33 130,33 C150,33 155,47 175,47 C195,47 200,36 220,36 C240,36 245,44 265,44 C285,44 290,37 310,37 C330,37 335,43 355,43 C375,43 380,39 400,39"
          />
          <path
            className="prana-wave prana-w2"
            d="M0,40 C15,40 20,24 40,24 C60,24 65,56 85,56 C105,56 110,28 130,28 C150,28 155,52 175,52 C195,52 200,32 220,32 C240,32 245,48 265,48 C285,48 290,35 310,35 C330,35 335,45 355,45 C375,45 380,38 400,38"
          />
          <path
            className="prana-wave prana-w1"
            d="M0,40 C15,40 20,10 40,10 C60,10 65,70 85,70 C105,70 110,20 130,20 C150,20 155,60 175,60 C195,60 200,30 220,30 C240,30 245,55 265,55 C285,55 290,25 310,25 C330,25 335,50 355,50 C375,50 380,35 400,35"
          />

          {/* Centre anchor dot — deep pink brand colour */}
          <circle className="prana-center" cx="200" cy="40" r="4" fill="#E56C98" />
        </g>
      </svg>
    </div>
  </>
);

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
const AudioRecorder: React.FC<AudioRecorderProps> = ({
  backend_uri,
  session_id,
  language,
  onTranscriptChange,
  onProcessStart,
  onOpenAIDataReady,
  onEkaDataReady,
  onAudioUrlReady,
}) => {
  const [recording, setRecording]       = useState(false);
  const [paused, setPaused]             = useState(false);
  const [audioBlob, setAudioBlob]       = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [playbackUrl, setPlaybackUrl]   = useState<string | null>(null);
  const [isPlaying, setIsPlaying]       = useState(false);

  const [majorLang, setMajorLang] = useState<string>('Hindi');
  const [minorLang, setMinorLang] = useState<string>('English');
  const [provider, setProvider]   = useState<Provider>('anthropic');
  const [fileName, setFileName]   = useState<string>('');
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef   = useRef<Blob[]>([]);
  const timerRef         = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef     = useRef<HTMLInputElement | null>(null);
  const audioElementRef  = useRef<HTMLAudioElement | null>(null);

  /* Revoke old object URL when blob changes */
  useEffect(() => {
    if (!audioBlob) { setPlaybackUrl(null); return; }
    const url = URL.createObjectURL(audioBlob);
    setPlaybackUrl(url);
    setIsPlaying(false);
    return () => URL.revokeObjectURL(url);
  }, [audioBlob]);

  const togglePlayback = () => {
    const el = audioElementRef.current;
    if (!el) return;
    if (el.paused) { el.play(); setIsPlaying(true); }
    else           { el.pause(); setIsPlaying(false); }
  };

  /* ── File upload handler ── */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) { setAudioBlob(file); generateResponse(file); }
  };

  /* ── Core pipeline ── */
  const generateResponse = async (manualBlob?: Blob) => {
    const blobToUse = manualBlob || audioBlob;
    if (!blobToUse) return;

    /* Ensure we have a valid session id — self-heal if the prop is empty
       (e.g. /session_init hadn't completed or failed on mount). */
    let sid = session_id;
    if (!sid) {
      try {
        const initRes = await axios.post(`${backend_uri}/session_init`, { language });
        sid = initRes.data?.session_id ?? '';
      } catch (e) {
        console.error('session_init failed:', e);
      }
    }
    if (!sid) {
      setUploadError('Could not start a session. Please refresh and try again.');
      return;
    }
    setUploadError(null);

    setIsGenerating(true);
    onProcessStart(provider);

    /* 0. Auto-save the recording to the user-chosen folder (best-effort) */
    try {
      const handle = await loadDirectoryHandle();
      if (handle && (await ensureReadWritePermission(handle))) {
        const savedPath = await saveAudioToFolder(
          handle,
          blobToUse,
          fileName.trim() || undefined,
        );
        setSaveStatus(`Saved to “${handle.name}/${savedPath}”`);
      } else {
        setSaveStatus(null);
      }
    } catch (err) {
      console.error('Local folder save failed:', err);
      setSaveStatus('Could not save to the chosen folder.');
    }

    /* 1. Upload audio to Firebase via backend */
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', blobToUse, 'recording.webm');
      uploadFormData.append('session_id', sid);
      const uploadRes = await axios.post(`${backend_uri}/upload_audio`, uploadFormData);
      onAudioUrlReady(uploadRes.data.audio_url);
    } catch (err) {
      console.error('Firebase upload failed:', err);
      setUploadError('Audio upload failed. Please try again.');
      setIsGenerating(false);
      return;
    }

    /* 2. Prepare form data */
    const formData = new FormData();
    formData.append('file', blobToUse, 'recording.webm');
    formData.append('session_id', sid);
    formData.append('language', language);
    formData.append('major_lang', majorLang);
    formData.append('minor_lang', minorLang);
    formData.append('provider', provider);

    const ekaFormData = new FormData();
    ekaFormData.append('file', blobToUse, 'recording.webm');
    ekaFormData.append('session_id', sid);

    try {
      if (provider === 'ekacare') {
        /* 3a. Eka.care pipeline (transcription + notes end-to-end) */
        const res = await axios.post(`${backend_uri}/process_ekacare`, ekaFormData);
        const ekaResult = res.data?.eka_data || null;
        if (ekaResult) {
          const transcriptText =
            ekaResult.transcript?.transcript ??
            (typeof ekaResult.transcript === 'string' ? ekaResult.transcript : '');
          if (transcriptText) onTranscriptChange(transcriptText);
        }
        onEkaDataReady(ekaResult);
      } else {
        /* 3b. OpenAI / Anthropic pipeline: Whisper STT, then provider-specific text steps */
        const response = await axios.post(`${backend_uri}/process_audio`, formData);
        const transcriptText = response.data.transcript;
        onTranscriptChange(transcriptText);

        if (response.data.no_speech_detected) {
          setUploadError(
            'No speech detected in this recording. Check your microphone (or that you uploaded the right file) and try again.',
          );
          onOpenAIDataReady(null);
          onEkaDataReady(null);
          return;
        }

        const baseBody = { session_id: sid, language, provider };

        /* Label speakers first: without Doctor/Patient labels the note-writer can't
           tell a doctor's question ("any diabetes?") from a patient's answer.
           Skipped when speech-to-text already labelled them (backend STT_ENGINE=diarize).
           If labelling fails or drops words, fall back to the raw transcript. */
        let labelledTranscript = transcriptText;
        if (!response.data.speakers_labelled) {
          try {
            const formatRes = await axios.post(`${backend_uri}/format_conversation`, {
              ...baseBody,
              transcript: transcriptText,
            });
            if (formatRes.data?.complete && formatRes.data?.formatted_conversation) {
              labelledTranscript = formatRes.data.formatted_conversation;
            }
          } catch (err) {
            console.error('Speaker labelling failed, using raw transcript:', err);
          }
        }

        const textBody = { ...baseBody, transcript: labelledTranscript };
        const [extractRes, summariseRes] = await Promise.allSettled([
          axios.post(`${backend_uri}/extract`, textBody),
          axios.post(`${backend_uri}/summarise`, textBody),
        ]);

        if (summariseRes.status === 'rejected') {
          console.error('Clinical note generation failed:', summariseRes.reason);
          setUploadError('Could not generate the clinical note. The transcript is shown below; please try again.');
        }
        if (extractRes.status === 'rejected') {
          console.error('Extraction failed:', extractRes.reason);
        }

        // Normalize to the eka_data shape so the display tabs + submit flow work unchanged
        const extractions: any[] =
          extractRes.status === 'fulfilled' ? extractRes.value.data?.extractions ?? [] : [];
        const emrData = Object.assign({}, ...extractions.map((ext: any) => {
          const key = Object.keys(ext).find(k => k !== 'veracity');
          return key ? { [key]: ext[key], [`${key}_verified`]: ext.veracity ? 'yes' : 'no' } : {};
        }));
        const normalized = {
          clinicalNotes: summariseRes.status === 'fulfilled' ? summariseRes.value.data.summary : '',
          emrData,
          transcript: labelledTranscript,
          removedStatements:
            summariseRes.status === 'fulfilled' ? summariseRes.value.data.removed ?? [] : [],
        };
        onEkaDataReady(normalized);
      }
    } catch (error) {
      console.error(`${provider} pipeline failed:`, error);
      onOpenAIDataReady(null);
      onEkaDataReady(null);
    } finally {
      setIsGenerating(false);
    }
  };

  /* ── Recording controls ── */
  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    audioChunksRef.current   = [];

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunksRef.current.push(e.data);
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      setAudioBlob(blob);
      if (timerRef.current) clearInterval(timerRef.current);
      setRecordingTime(0);
      /* Release the mic */
      stream.getTracks().forEach(track => track.stop());
    };

    mediaRecorder.start();
    setRecording(true);
    setPaused(false);
    setRecordingTime(0);
    timerRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
  };

  const pauseRecording = () => {
    const mr = mediaRecorderRef.current;
    if (!mr || mr.state !== 'recording') return;
    mr.pause();
    setPaused(true);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const resumeRecording = () => {
    const mr = mediaRecorderRef.current;
    if (!mr || mr.state !== 'paused') return;
    mr.resume();
    setPaused(false);
    timerRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
    setPaused(false);
  };

  const formatTime = (s: number) => {
    const m   = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  /* ── Shared styles ── */
  const LANGUAGES = ['Hindi', 'English', 'Marathi', 'Chhattisgarhiya', 'Gujarati'];

  const selectStyle: React.CSSProperties = {
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    width: '100%',
    padding: '10px 36px 10px 14px',
    fontSize: 13,
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    color: '#1F2332',
    background:
      "#FFFFFF url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235B6591' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\") no-repeat right 12px center",
    border: '1px solid #DEE2F1',
    borderRadius: 8,
    cursor: 'pointer',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: '#5B6591',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 6,
  };

  /* ─────────────────────────────────────────
     Render
  ───────────────────────────────────────── */
  return (
    <div
      style={{
        fontFamily: 'Inter, sans-serif',
        background: '#FFFFFF',
        border: '1px solid #DEE2F1',
        borderRadius: 14,
        padding: 24,
        boxShadow: '0 1px 2px rgba(31, 35, 50, 0.04)',
      }}
    >
      {/* ── Language + AI provider pickers ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 14,
          marginBottom: 20,
        }}
      >
        <div>
          <label style={labelStyle}>Majority Language</label>
          <select
            value={majorLang}
            onChange={(e) => setMajorLang(e.target.value)}
            style={selectStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#5ab4e7';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(90, 180, 231, 0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#DEE2F1';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Minority Language</label>
          <select
            value={minorLang}
            onChange={(e) => setMinorLang(e.target.value)}
            style={selectStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#5ab4e7';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(90, 180, 231, 0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#DEE2F1';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {[...LANGUAGES.slice(1), LANGUAGES[0]].map((l) => <option key={l}>{l}</option>)}
          </select>
        </div>

        <div>
          <label style={labelStyle}>AI Model</label>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value as Provider)}
            style={selectStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#5ab4e7';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(90, 180, 231, 0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#DEE2F1';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <option value="ekacare">Eka.care</option>
            <option value="openai">OpenAI</option>
            <option value="anthropic">Anthropic (Claude)</option>
          </select>
        </div>
      </div>

      {/* ── Auto-save folder picker ── */}
      <AudioFolderPicker onChange={() => setSaveStatus(null)} />

      {/* ── File name ── */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle} htmlFor="recording-file-name">
          File name
        </label>
        <input
          id="recording-file-name"
          type="text"
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
          placeholder="e.g. john-doe-followup (saved in today's date folder)"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '10px 14px',
            fontSize: 13,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            color: '#1F2332',
            background: '#FFFFFF',
            border: '1px solid #DEE2F1',
            borderRadius: 8,
            outline: 'none',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = '#5ab4e7';
            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(90, 180, 231, 0.15)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = '#DEE2F1';
            e.currentTarget.style.boxShadow = 'none';
          }}
        />
        <p style={{ margin: '6px 2px 0', fontSize: 11, color: '#8A93B2' }}>
          Saved as <code>&lt;chosen folder&gt;/{new Date().toISOString().slice(0, 10)}/{(fileName.trim() || 'recording-…')}.webm</code>. Leave blank to auto-name.
        </p>
      </div>

      {/* ── Recording stage ── */}
      <div
        style={{
          background: recording
            ? 'radial-gradient(circle at 50% 0%, #F2F3FA 0%, #FBF7F2 100%)'
            : '#F9FAFB',
          border: `1px solid ${recording ? '#9ed4ef' : '#E5E7EB'}`,
          borderRadius: 12,
          padding: '20px 20px 16px',
          marginBottom: 16,
          transition: 'background 0.25s ease, border-color 0.25s ease',
        }}
      >
        {/* Status row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: recording ? 14 : 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: recording ? (paused ? '#F59E0B' : '#F5222D') : '#D0D5DD',
                boxShadow: recording
                  ? `0 0 0 4px ${paused ? 'rgba(245, 158, 11, 0.18)' : 'rgba(245, 34, 45, 0.18)'}`
                  : 'none',
                animation: recording && !paused ? 'recordingDot 1.4s ease-in-out infinite' : 'none',
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: recording ? '#1F2332' : '#667085',
                letterSpacing: '0.01em',
              }}
            >
              {recording
                ? paused
                  ? 'Recording paused'
                  : 'Recording in progress'
                : audioBlob
                  ? 'Recording ready'
                  : 'Ready to record'}
            </span>
          </div>

          {recording && (
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontVariantNumeric: 'tabular-nums',
                fontSize: 14,
                fontWeight: 600,
                color: '#3E4665',
                background: '#FFFFFF',
                border: '1px solid #DEE2F1',
                borderRadius: 6,
                padding: '3px 10px',
              }}
            >
              {formatTime(recordingTime)}
            </span>
          )}
        </div>

        {/* ── Siri waveform (replaces the old <canvas>) ── */}
        {recording && !paused && <SiriWaveform />}

        {/* Playback row */}
        {!recording && playbackUrl && (
          <div
            style={{
              marginTop: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 12px',
              background: '#FFFFFF',
              border: '1px solid #DEE2F1',
              borderRadius: 8,
            }}
          >
            <button
              type="button"
              onClick={togglePlayback}
              aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
              style={{
                width: 36,
                height: 36,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #5ab4e7 0%, #4a5585 100%)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(74, 85, 133, 0.25)',
                flexShrink: 0,
              }}
            >
              <i
                className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'}`}
                style={{ fontSize: 12 }}
              />
            </button>
            <audio
              ref={audioElementRef}
              src={playbackUrl}
              controls
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              style={{ flex: 1, height: 36 }}
            />
          </div>
        )}
      </div>

      {/* ── Action buttons ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 10,
        }}
      >
        {/* Start  ·  or  ·  Pause/Resume + Stop while recording */}
        {!recording ? (
          <button
            onClick={startRecording}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '11px 16px',
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              color: '#FFFFFF',
              background: 'linear-gradient(135deg, #3E4665 0%, #2d334a 100%)',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(31, 35, 50, 0.15)',
              transition: 'transform 0.12s ease, box-shadow 0.12s ease, opacity 0.12s ease',
            }}
            onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(1px)')}
            onMouseUp={(e)   => (e.currentTarget.style.transform = 'translateY(0)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
          >
            <i className="fas fa-microphone" style={{ fontSize: 12 }} />
            Start Recording
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Pause / Resume */}
            <button
              onClick={paused ? resumeRecording : pauseRecording}
              style={{
                flex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '11px 16px',
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                fontWeight: 600,
                color: '#FFFFFF',
                background: paused
                  ? 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)'
                  : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                boxShadow: paused
                  ? '0 1px 2px rgba(22, 163, 74, 0.25)'
                  : '0 1px 2px rgba(245, 158, 11, 0.25)',
                transition: 'transform 0.12s ease, box-shadow 0.12s ease, opacity 0.12s ease',
              }}
              onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(1px)')}
              onMouseUp={(e)   => (e.currentTarget.style.transform = 'translateY(0)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <i className={`fas ${paused ? 'fa-play' : 'fa-pause'}`} style={{ fontSize: 12 }} />
              {paused ? 'Resume' : 'Pause'}
            </button>

            {/* Stop */}
            <button
              onClick={stopRecording}
              style={{
                flex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '11px 16px',
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                fontWeight: 600,
                color: '#FFFFFF',
                background: 'linear-gradient(135deg, #F5222D 0%, #C0392B 100%)',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(245, 34, 45, 0.25)',
                transition: 'transform 0.12s ease, box-shadow 0.12s ease, opacity 0.12s ease',
              }}
              onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(1px)')}
              onMouseUp={(e)   => (e.currentTarget.style.transform = 'translateY(0)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <i className="fas fa-stop" style={{ fontSize: 12 }} />
              Stop
            </button>
          </div>
        )}

        {/* Upload audio */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="audio/*"
          onChange={handleFileChange}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isGenerating}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '11px 16px',
            fontFamily: 'Inter, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            color: '#3E4665',
            background: '#FFFFFF',
            border: '1px solid #DEE2F1',
            borderRadius: 8,
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            opacity: isGenerating ? 0.5 : 1,
            transition: 'background 0.12s ease, border-color 0.12s ease',
          }}
          onMouseEnter={(e) => {
            if (isGenerating) return;
            e.currentTarget.style.background = '#F2F3FA';
            e.currentTarget.style.borderColor = '#5D6897';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#FFFFFF';
            e.currentTarget.style.borderColor = '#DEE2F1';
          }}
        >
          <i className="fas fa-file-upload" style={{ fontSize: 12 }} />
          Upload Audio
        </button>

        {/* Generate insights */}
        <button
          onClick={() => generateResponse()}
          disabled={!audioBlob || isGenerating}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '11px 16px',
            fontFamily: 'Inter, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            color: '#FFFFFF',
            background:
              !audioBlob || isGenerating
                ? '#9ed4ef'
                : 'linear-gradient(135deg, #5ab4e7 0%, #4a5585 100%)',
            border: 'none',
            borderRadius: 8,
            cursor: !audioBlob || isGenerating ? 'not-allowed' : 'pointer',
            boxShadow:
              !audioBlob || isGenerating
                ? 'none'
                : '0 1px 2px rgba(74, 85, 133, 0.25)',
            transition: 'transform 0.12s ease, opacity 0.12s ease',
          }}
          onMouseDown={(e) => {
            if (!audioBlob || isGenerating) return;
            e.currentTarget.style.transform = 'translateY(1px)';
          }}
          onMouseUp={(e)   => (e.currentTarget.style.transform = 'translateY(0)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
        >
          {isGenerating ? (
            <>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: 12 }} />
              Processing
            </>
          ) : (
            <>
              <i className="fas fa-bolt" style={{ fontSize: 12 }} />
              Generate Insights
            </>
          )}
        </button>
      </div>

      {/* Upload / session error */}
      {uploadError && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 12,
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            fontWeight: 500,
            color: '#DC2626',
          }}
        >
          <i className="fas fa-exclamation-circle" style={{ fontSize: 12 }} />
          {uploadError}
        </div>
      )}

      {/* Save-to-folder confirmation */}
      {saveStatus && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 12,
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            fontWeight: 500,
            color: '#16A34A',
          }}
        >
          <i className="fas fa-check-circle" style={{ fontSize: 12 }} />
          {saveStatus}
        </div>
      )}

      {/* Global keyframe for the rec dot (used inline above) */}
      <style>{`
        @keyframes recordingDot {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.45; }
        }
      `}</style>
    </div>
  );
};

export default AudioRecorder;